import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { Box, Tabs, Tab } from "@mui/material";

import LensView from "./m-calendar/LensView.tsx";

import SearchIcon from "@mui/icons-material/Search";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PreviewIcon from "@mui/icons-material/Preview";
import PinIcon from "@mui/icons-material/Pin";
import ApprovalIcon from "@mui/icons-material/Approval";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InfoIcon from "@mui/icons-material/Info";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";

import { useAppSelector } from "../../store/hooks.ts";
import ExcelGenerator from "../excelgenerator/ExcelGenerator.tsx";
import Legend from "./m-calendar/Legend.tsx";
import Search from "./m-search/Search.tsx";
import SettingsSearch from "./m-settings/search.tsx";
import MiniCalendar from "./m-calendar/MiniCalendar.tsx";
import ItemNumbers from "./m-calendar/ItemNumbers.tsx";

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`vertical-tabpanel-${index}`}
			aria-labelledby={`vertical-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 1 }}>{children}</Box>}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `vertical-tab-${index}`,
		"aria-controls": `vertical-tabpanel-${index}`,
	};
}

const MiniNav = (props) => {
	const [value, setValue] = useState(0);
	const location = useLocation(); // Get current path

	// Reset tab value to `0` when the URL path changes
	useEffect(() => {
		// Reset the tab index to 0 when navigating to a new path
		setValue(0);
	}, [location.pathname]);

	const boxRef = useRef<HTMLDivElement | null>(null);

	const activeTab = useAppSelector((state) => state.settings.activeTab);
	const currentLens = useAppSelector((state) => state.calendar.lens); // On change, set mininav to Legend

	// Function to dynamically return the icon and component for the settings tabs
	const getSettingsIconAndComponent = React.useCallback(() => {
		switch (activeTab) {
			case 1:
				return {
					icon: <ManageSearchIcon fontSize="large" />,
					component: <SettingsSearch miniNavRef={boxRef} />,
				};
			case 0:
			case 2:
			default:
				return { icon: null, component: null };
		}
	}, [activeTab, boxRef]);

	// Icon and component mapping based on URL paths
	const panelMapping = React.useMemo(
		() => ({
			"/": [
				{
					icon: <CalendarMonthIcon fontSize="large" />,
					label: "Mini Calendar View",
					component: <MiniCalendar />,
				},
				{
					icon: <PreviewIcon fontSize="large" />,
					label: "Lens View",
					component: <LensView />,
				},
				{
					icon: <PinIcon fontSize="large" />,
					label: "Item Numbers",
					component: <MiniCalendar />,
				},
				{
					icon: <FileDownloadIcon fontSize="large" />,
					label: "Export File",
					component: <ExcelGenerator />,
				},
				{
					icon: <InfoIcon fontSize="large" />,
					label: "Legend",
					component: <Legend />,
				},
			],
			"/search": [
				{
					icon: <SearchIcon fontSize="large" />,
					label: "Search",
					component: <Search />,
				},
			],
			"/calendar": [
				{
					icon: <CalendarMonthIcon fontSize="large" />,
					label: "Mini Calendar View",
					component: <MiniCalendar />,
				},
				{
					icon: <PreviewIcon fontSize="large" />,
					label: "Calendar View",
					component: <LensView />,
				},
				{
					icon: <PinIcon fontSize="large" />,
					label: "Item Numbers",
					component: <ItemNumbers />,
				},
				{
					icon: <FileDownloadIcon fontSize="large" />,
					label: "Export File",
					component: <ExcelGenerator />,
				},
				{
					icon: <InfoIcon fontSize="large" />,
					label: "Legend",
					component: <Legend />,
				},
			],
			"/settings": [getSettingsIconAndComponent()],
			"/profile": [
				{
					icon: <ApprovalIcon fontSize="large" />,
					label: "Profile",
					component: <span>hello</span>,
				},
			],
			// Add more paths and icons as needed
		}),
		[getSettingsIconAndComponent]
	);

	// Get icons and content components based on current URL path
	const iconsAndComponents = panelMapping[location.pathname] || [];

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	// Build the panels for this route (settings depends on activeTab)
	const panels = React.useMemo(() => {
		const arr = panelMapping[location.pathname] || [];
		return arr.filter(Boolean); // guard against nulls
	}, [location.pathname, panelMapping]);

	// Where is "Legend" on this route?
	const legendIndex = React.useMemo(
		() => panels.findIndex((p) => p?.label === "Legend"),
		[panels]
	);

	// Track previous lens to detect real changes
	const prevLensRef = React.useRef(currentLens);

	// ✅ Only jump to Legend when lens ACTUALLY changes
	useEffect(() => {
		if (currentLens !== prevLensRef.current) {
			prevLensRef.current = currentLens;
			if (legendIndex !== -1) setValue(legendIndex);
		}
	}, [currentLens, legendIndex, location.pathname]);

	return (
		<Box
			ref={boxRef}
			sx={{
				flexGrow: 1,
				bgcolor: "background.paper",
				display: "flex",
				borderRight: "solid 2px #B4C9D0",
				...props.style,
			}}
		>
			<Tabs
				orientation="vertical"
				variant="scrollable"
				scrollButtons={false}
				value={value}
				onChange={handleChange}
				aria-label="Vertical tabs example"
				sx={{
					borderRight: 1,
					borderColor: "divider",
					width: "50px",
					minWidth: "50px", // Prevent shrinking
					maxWidth: "50px",
					background: "#C5E5F0",
					"& .MuiTabs-flexContainer": {
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-start",
						alignItems: "center",
						height: "100%",
					},
				}}
			>
				{iconsAndComponents.map(
					(item, index) =>
						item.icon && (
							<Tab
								key={index}
								icon={item.icon}
								{...a11yProps(index)}
								sx={{ marginTop: "10px", marginBottom: "10px" }} // Add spacing between the icons
							/>
						)
				)}
			</Tabs>

			{iconsAndComponents.map((item, index) => (
				<TabPanel
					key={index}
					value={value}
					index={index}
					style={{
						height: "100%",
						flexGrow: 1,
						overflowX: "hidden",
						overflowY: "auto",
						background: "#F2F8FA",
					}}
				>
					{item.component} {/* Display imported component */}
				</TabPanel>
			))}
		</Box>
	);
};

export default MiniNav;
